import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import type { AuthConfigResponse, MeResponse, SessionResponse } from '@lp/shared';
import {
  api,
  ApiError,
  getViewAs,
  onAuthLostHandler,
  refreshSession,
  request,
  setAccessToken,
  setViewAs,
} from './api.js';

type AuthState =
  | { status: 'loading' }
  | { status: 'anonymous' }
  | { status: 'authenticated'; me: MeResponse };

type AuthContextValue = {
  state: AuthState;
  config: AuthConfigResponse | null;
  devLogin: (email: string) => Promise<void>;
  logout: () => Promise<void>;
  reload: () => Promise<void>;
  /** Admin only: see the platform as `userId` would, read-only, in this tab. */
  startViewAs: (userId: string) => Promise<void>;
  stopViewAs: () => Promise<void>;
};

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>({ status: 'loading' });
  const [config, setConfig] = useState<AuthConfigResponse | null>(null);
  const renewTimer = useRef<number | null>(null);

  const loadMe = useCallback(async () => {
    try {
      const me = await api.get<MeResponse>('/auth/me');
      setState({ status: 'authenticated', me });
    } catch (err) {
      // A view that can no longer be honoured (the person was deactivated,
      // or the viewer is no longer an admin) ends, and the admin is back as
      // themselves rather than signed out.
      if (getViewAs() && err instanceof ApiError && err.status !== 401) {
        setViewAs(null);
        await loadMe();
        return;
      }
      setState({ status: 'anonymous' });
    }
  }, []);

  // Boot: try the refresh cookie once. A fresh browser simply lands anonymous.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const cfg = await api.anonymous
        .get<AuthConfigResponse>('/auth/config')
        .catch(() => null);
      if (!cancelled && cfg) setConfig(cfg);

      const token = await refreshSession();
      if (cancelled) return;
      if (!token) {
        setState({ status: 'anonymous' });
        return;
      }
      await loadMe();
    })();
    return () => {
      cancelled = true;
    };
  }, [loadMe]);

  // Keep the in-memory access token warm so a long reading session does not
  // hit a 401 on the first click after a break.
  useEffect(() => {
    if (state.status !== 'authenticated') return;
    const tick = () => {
      void refreshSession().then((token) => {
        if (!token) setState({ status: 'anonymous' });
      });
    };
    renewTimer.current = window.setInterval(tick, 10 * 60 * 1000);
    return () => {
      if (renewTimer.current) window.clearInterval(renewTimer.current);
    };
  }, [state.status]);

  useEffect(() => {
    onAuthLostHandler(() => setState({ status: 'anonymous' }));
    return () => onAuthLostHandler(null);
  }, []);

  const devLogin = useCallback(
    async (email: string) => {
      const res = await api.anonymous.post<SessionResponse>('/auth/dev-login', { email });
      setAccessToken(res.accessToken, res.expiresInSec);
      await loadMe();
    },
    [loadMe],
  );

  const logout = useCallback(async () => {
    const res = await api
      .post<{ ok: boolean; idpLogoutUrl: string | null }>('/auth/logout')
      .catch(() => null);
    setAccessToken(null);
    setViewAs(null);
    setState({ status: 'anonymous' });
    if (res?.idpLogoutUrl) window.location.href = res.idpLogoutUrl;
  }, []);

  // Both directions reload the page: every cached query was answered for the
  // other person, and a fresh start is the only way to be sure none survives.
  const startViewAs = useCallback(async (userId: string) => {
    await request('/admin/view-as', { method: 'POST', body: { userId }, asSelf: true });
    setViewAs(userId);
    window.location.assign('/');
  }, []);

  const stopViewAs = useCallback(async () => {
    const userId = getViewAs();
    setViewAs(null);
    if (userId) {
      await request('/admin/view-as/stop', { method: 'POST', body: { userId }, asSelf: true }).catch(
        () => {
          // The audit line is a courtesy; failing it must not trap the admin.
        },
      );
    }
    window.location.assign('/admin/users');
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({ state, config, devLogin, logout, reload: loadMe, startViewAs, stopViewAs }),
    [state, config, devLogin, logout, loadMe, startViewAs, stopViewAs],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>');
  return ctx;
}

/** The signed-in user, or throw. Use inside routes that require auth. */
export function useMe(): MeResponse {
  const { state } = useAuth();
  if (state.status !== 'authenticated') {
    throw new Error('useMe used outside an authenticated route');
  }
  return state.me;
}
